import { MemberStatus } from "@upsquad/shared";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getIsStaff, getUser } from "@/src/auth/store/auth-selectors";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Spinner } from "@/src/common/components/ui";
import { COMMON_ROUTES } from "@/src/common/constants/common-routes";
import { getSubdomain } from "@/src/common/lib/subdomain";
import { useAppSelector } from "@/src/common/redux/store";
import { WelcomeJoinModal } from "@/src/onboarding/components/modals/welcome-join-modal";
import { ONBOARDING_ROUTES } from "@/src/onboarding/constants/onboarding-routes";
import { useJoinCommunityMutation } from "@/src/onboarding/services/onboarding-http.service";
import { POST_ROUTES } from "@/src/post/constants/post-routes";

function isHttpError(error: unknown): error is { status: number } {
	return typeof error === "object" && error !== null && "status" in error && typeof (error as { status: unknown }).status === "number";
}

export default function JoinCommunityPage() {
	const navigate = useNavigate();
	const user = useAppSelector(getUser);
	const isStaff = useAppSelector(getIsStaff);
	const [joinCommunity] = useJoinCommunityMutation();
	const [showWelcome, setShowWelcome] = useState(false);
	const [communitySlug, setCommunitySlug] = useState("");

	const subdomain = getSubdomain();

	useEffect(() => {
		if (!subdomain) return;
		setCommunitySlug(subdomain);

		if (isStaff) {
			navigate(POST_ROUTES.FEED, { replace: true });
			return;
		}

		joinCommunity(subdomain)
			.unwrap()
			.then((result) => {
				if (result && "status" in result) {
					if (result.status === MemberStatus.APPROVED) {
						setShowWelcome(true);
					} else {
						navigate(ONBOARDING_ROUTES.PENDING_JOIN_COMMUNITY);
					}
				}
			})
			.catch((err: unknown) => {
				if (isHttpError(err) && err.status === 404) {
					navigate(COMMON_ROUTES.COMMUNITY_NOT_FOUND);
					return;
				}
				navigate(ONBOARDING_ROUTES.PENDING_JOIN_COMMUNITY);
			});
	}, [subdomain, joinCommunity, navigate, isStaff]);

	return (
		<>
			<PageHelmet title="Join Community" />
			<div className="flex min-h-screen items-center justify-center">{!showWelcome && <Spinner size="lg" />}</div>
			<WelcomeJoinModal isOpen={showWelcome} firstName={user?.first_name ?? ""} communitySlug={communitySlug} />
		</>
	);
}
